import React, { useRef, useState } from 'react'
import { DateTime } from 'luxon'

import './styles.scss'
import { intranetApi } from 'api'
import { Button, SelectWithLabel } from 'components/Form'
import ModalDelete from 'components/ModalDelete'
import { getBodyStyle, locales } from 'components/TranslationsView/utils'
import { lang } from 'utils/helpers'
import { getStaticImage } from 'utils/statics'
import SaveToastNotification from 'utils/toasts/SaveToastNotification'

const TranslationItem = props => {
  const {
    id, tag, platform, labels, textDefault, localeDisplay,
    text, date, translations, setTranslations, labelsOptions
  } = props
  const bodyRef = useRef()
  const [isOpen, setIsOpen] = useState(false)
  const [isOpenRemoveModal, setIsOpenRemoveModal] = useState(false)
  const [labelsValue, setLabelsValue] = useState(labels?.length ? labels.map(({ _id, name }) => ({ value: _id, label: name })) : [])
  const [textValue, setTextValue] = useState(text || {})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [loadingModal, setLoadingModal] = useState(false)
  const [errorModal, setErrorModal] = useState('')

  const toggle = () => setIsOpen(prevState => !prevState)

  const toggleRemoveModal = () => setIsOpenRemoveModal(prevState => !prevState)

  const disabledSave = () => {
    for (const locale of locales) {
      if (textValue[locale] !== text[locale]) return false
    }
    if (labelsValue?.length !== labels?.length) return false
    if (labelsValue?.length) {
      for (const { value } of labelsValue) {
        if (!labels.some(({ _id }) => _id === value)) return false
      }
    }
    return true
  }

  const handleSave = () => {
    if (error) setError('')
    setLoading(true)

    const data = { id, text: textValue }
    if (labelsValue?.length) data.labels = labelsValue.map(({ value }) => value)

    intranetApi.translations.updateTranslation(data)
      .then(({ data }) => {
        const updatedTranslations = [...translations]
        const translationIndex = updatedTranslations.findIndex(({ _id }) => _id === id)
        updatedTranslations[translationIndex] = {
          ...updatedTranslations[translationIndex],
          ...data
        }
        toggle()
        setTranslations(updatedTranslations)
        SaveToastNotification.ok({ text: 'dashboard_translation-updated-successfully' })
      })
      .catch(() => {
        SaveToastNotification.error({ text: 'dashboard_error-updating-translation' })
      })
      .finally(() => setLoading(false))
  }

  const handleRemove = () => {
    if (errorModal) setErrorModal('')
    setLoadingModal(true)
    intranetApi.translations.removeTranslation({ id })
      .then(() => {
        const updatedTranslations = [...translations]
        const translationIndex = updatedTranslations.findIndex(({ _id }) => _id === id)
        updatedTranslations.splice(translationIndex, 1)
        setTranslations(updatedTranslations)
        toggle()
        toggleRemoveModal()
        SaveToastNotification.ok({ text: 'dashboard_translation-removed-successfully' })
      })
      .catch(() => {
        SaveToastNotification.error({ text:'dashboard_error-deleting-translation' })
      })
      .finally(() => setLoadingModal(false))
  }

  return (
    <div className='translations-translations-section-item'>
      <div className='translations-translations-section-item__header' onClick={toggle}>
        <div className='translations-translations-section-item__content'>
          <code
            className='translations-translations-section-item__tag text-ellipsis'
            onClick={(e) => e.stopPropagation()}
            title={tag}
          >
            {tag}
          </code>
          <span className='translations-translations-section-item__platform'>{platform}</span>
          {(!isOpen && labels?.length) ? (
            <div className='translations-translations-section-item__labels'>
              {labels.map((label, index) => index <= 1 && (
                <span
                  key={label._id}
                  className='translations-translations-section-item__label text-ellipsis'
                  title={label.name}
                  style={{ backgroundColor: label.color || null }}
                >
                  {label.name}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        {!isOpen ? (
          <div className='translations-translations-section-item__content'>
            <p
              className='translations-translations-section-item__text text-ellipsis'
              title={text[localeDisplay] || textDefault}
            >
              {text[localeDisplay] || textDefault}
            </p>
            {date ? (
              <span className='translations-translations-section-item__date'>
                {DateTime.fromISO(date).toFormat('yyyy-LL-dd T')}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      <div
        ref={bodyRef}
        id={id}
        className='translations-translations-section-item__body'
        style={getBodyStyle({ isOpen, maxHeight: `${bodyRef?.current?.scrollHeight || 0}px` })}
      >
        <div className='translations-translations-section-item__body-content'>
          <div className='translations-translations-section-item__body-section'>
            <div className='translations-translations-section-item__textarea-container'>
              {locales.map(locale => (
                <div key={locale} className='translations-translations-section-item__text-area'>
                  <span
                    className='translations-translations-section-item__locale-label'
                    title={lang(`locale_${locale}`)}
                  >
                    {locale.toLocaleUpperCase()}
                  </span>
                  <textarea
                    placeholder={lang('dashboard_type-something')}
                    value={textValue[locale]}
                    onChange={({ target: { value } }) => setTextValue(prevState => ({ ...prevState, [locale]: value }))}
                  />
                  {textValue[locale] ? (
                    <button
                      className='translations-translations-section-item__btn'
                      type='button'
                      onClick={() => setTextValue(prevState => ({ ...prevState, [locale]: '' }))}
                    >
                      <figure className='translations-translations-section-item__icon'>
                        <img src={getStaticImage('/icons/orange-brand/icon-close-slim-grey.svg')} alt='Clean' />
                      </figure>
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
            <div className='translations-translations-section-item__selector-container'>
              <SelectWithLabel
                multi
                isClearable
                label={lang('dashboard_labels')}
                placeholder={lang('dashboard_select')}
                options={labelsOptions}
                value={labelsValue}
                onChange={value => setLabelsValue(!value ? [] : value)}
              />
              {(isOpen && labels?.length) ? (
                <div className='translations-translations-section-item__labels'>
                  {labels.map(label => (
                    <span
                      key={label._id}
                      className='translations-translations-section-item__label text-ellipsis'
                      title={label.name}
                      style={{ backgroundColor: label.color || null }}
                    >
                      {label.name}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
          <div className='translations-translations-section-item__buttons-container'>
            <Button
              remove
              className='translations-translations-section-item__btn-custom translations-translations-section-item__btn-custom--remove'
              value={lang('dashboard_delete')}
              onClick={toggleRemoveModal}
            />
            <Button
              className='translations-translations-section-item__btn-custom'
              value={lang('dashboard_cancel')}
              onClick={toggle}
            />
            <Button
              secondary
              className='translations-translations-section-item__btn-custom'
              value={lang('dashboard_save')}
              onClick={handleSave}
              isSubmitting={loading}
              disabled={disabledSave()}
            />
          </div>
        </div>
      </div>

      {/* Remove modal */}
      <ModalDelete
        isOpen={isOpenRemoveModal}
        toggle={toggleRemoveModal}
        event={handleRemove}
        header={lang('dashboard_delete-translation')}
        text1={lang('dashboard_sure-delete-translation')}
        text2={lang('dashboard_action-cannot-reversed')}
        isSubmitting={loadingModal}
        errorMessage={errorModal}
      />
    </div>
  )
}

export default TranslationItem
